import { useRef, useState } from "react";
import { Loader2, Paperclip, X } from "lucide-react";
import type { MaterialAttachmentDTO } from "@app/shared";
import { uploadAttachment } from "../lib/api";
import { useToast } from "./Toast";

/**
 * A file picker that uploads ONE attachment (image → post photo; PDF/TXT/MD →
 * AI reference context) via POST /attachments and reports the stored descriptor.
 * Reused by Custom, Carousel, and the AI-revise form.
 */
export function AttachmentInput({
  value,
  onChange,
  disabled,
  hint,
}: {
  value: MaterialAttachmentDTO | null;
  onChange: (a: MaterialAttachmentDTO | null) => void;
  disabled?: boolean;
  hint?: string;
}) {
  const toast = useToast();
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file: File) => {
    setBusy(true);
    try {
      const dto = await uploadAttachment(file);
      onChange(dto);
      toast.success(`Lampiran: ${dto.originalName}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload lampiran gagal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg,image/webp,application/pdf,text/plain,.md"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pick(f);
          e.target.value = "";
        }}
      />
      {value ? (
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <Paperclip className="h-4 w-4 shrink-0 text-teal" />
          <span className="min-w-0 flex-1 truncate text-slate-700">
            {value.originalName}
          </span>
          <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
            {value.kind === "image" ? "gambar" : "dokumen"}
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            aria-label="Hapus lampiran"
            className="shrink-0 text-slate-400 hover:text-red-600 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={disabled || busy}
          className="flex w-full items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-500 transition-colors hover:bg-slate-50 hover:text-navy disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Paperclip className="h-4 w-4" />
          )}
          Lampirkan gambar / dokumen (opsional)
        </button>
      )}
      {hint ? <p className="text-xs text-slate-400">{hint}</p> : null}
    </div>
  );
}
